import { Component, Host, h, Method, Prop, Watch } from '@stencil/core';
import { AppearanceMode, isAppearanceMode } from '../../entities';

@Component({
  tag: 'me-appearance-options',
  styleUrl: 'me-appearance-options.scss',
  shadow: false,
})
export class MeAppearanceOptions {

  private get storedAppearanceMode(): AppearanceMode {
    const mode = localStorage.getItem('me-appearance-mode');

    return isAppearanceMode(mode)
      ? mode
      : 'system';
  }

  private set storedAppearanceMode(value) {
    localStorage.setItem('me-appearance-mode', value);
  }

  private get storedFontSize(): number {
    return Number(localStorage.getItem('me-font-size') || this.fontSizeMin);
  }

  private set storedFontSize(value) {
    localStorage.setItem('me-font-size', String(value));
  }

  @Prop({ mutable: true }) mode?: AppearanceMode;
  @Watch('mode')
  handleModeChanged() {
    this.storedAppearanceMode = this.mode;

    document.body.classList.remove('light-mode', 'dark-mode');

    switch (this.mode) {
      case 'light':
        document.body.classList.add('light-mode');
        break;
      case 'dark':
        document.body.classList.add('dark-mode');
        break;
    }
  }

  @Prop({ mutable: true }) fontSize?: number;
  @Watch('fontSize')
  handleFontSizeChanged() {
    this.storedFontSize = this.fontSize;

    const rootElement = document.querySelector(':root');
    if (!(rootElement instanceof HTMLHtmlElement)) { return; }
    rootElement.style.fontSize = this.fontSize + 'px';
  }

  private readonly modeLabelMap: { [mode in AppearanceMode]: string } = {
    dark: 'Dunkel',
    light: 'Hell',
    system: 'System',
  };

  private readonly fontSizeMin = 16;
  private readonly fontSizeMax = 28;

  componentWillLoad() {
    this.mode = this.storedAppearanceMode;
    this.fontSize = this.storedFontSize;
  }

  render() {
    return (
      <Host class="me-appearance-options">
        <me-button
          theme="text"
          class="me-appearance-options__font-size-symbol me-appearance-options__font-size-symbol--r"
          onClick={this.decreaseFontSize.bind(this)}
        >
          A
        </me-button>
        <input
          class="me-appearance-options__font-size-range"
          type={'range'}
          min={this.fontSizeMin}
          max={this.fontSizeMax}
          value={this.fontSize}
          onChange={this.updateFontSize.bind(this)}
        />
        <me-button
          theme="text"
          class="me-appearance-options__font-size-symbol me-appearance-options__font-size-symbol--xl"
          onClick={this.increaseFontSize.bind(this)}
        >
          A
        </me-button>
        <me-separator direction="vertical" />
        <div class="me-appearance-options__mode">
          {this.mode === 'system'
            ? (
              <me-button
                round={true}
                theme="primary"
                title="Zum Dark-Mode wechseln"
                onClick={this.switchMode.bind(this)}
              >
                <i class="fa-solid fa-circle-half-stroke fa-fw" />
              </me-button>
            )
            : ''
          }
          {this.mode === 'dark'
            ? (
              <me-button
                round={true}
                theme="primary"
                title="Zum Light-Mode wechseln"
                onClick={this.switchMode.bind(this)}
              >
                <i class="fa-solid fa-moon fa-fw" />
              </me-button>
            )
            : ''
          }
          {this.mode === 'light'
            ? (
              <me-button
                round={true}
                theme="primary"
                title="Zum System-Theme wechseln"
                onClick={this.switchMode.bind(this)}
              >
                <i class="fa-solid fa-lightbulb fa-fw" />
              </me-button>
            )
            : ''
          }
          {this.modeLabelMap[this.mode]}
        </div>
      </Host>
    );
  }

  @Method()
  async switchMode() {
    switch (this.mode) {
      case 'system':
        this.mode = 'dark';
        break;
      case 'dark':
        this.mode = 'light';
        break;
      case 'light':
        this.mode = 'system';
        break;
    }
  }

  @Method()
  async increaseFontSize() {
    this.fontSize = Math.min(this.fontSize + 1, this.fontSizeMax);
  }

  @Method()
  async decreaseFontSize() {
    this.fontSize = Math.max(this.fontSize - 1, this.fontSizeMin);
  }

  private updateFontSize(event: Event) {
    if (!(event.target instanceof HTMLInputElement)) { return; }
    this.fontSize = event.target.valueAsNumber;
  }

}
